import React from 'react';
import { Card, CardContent } from './ui/card';
import { CheckCircle, Award, Briefcase, GraduationCap } from 'lucide-react';

export const About = () => {
  return (
    <section id="about" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-gray-900 mb-8 text-center">
            Licensed Mortgage Specialist with a CPA Background
          </h2>
          
          <p className="text-xl text-gray-600 mb-8 leading-relaxed text-center">
            Unlike typical mortgage loan officers, I bring a unique dual perspective as both a 
            <strong> Licensed Mortgage Specialist and Certified Public Accountant</strong>. 
            This combination allows me to evaluate reverse mortgages through a lens that most competitors can't offer.
          </p>

          {/* Photo floated left with text wrapping */}
          <div className="mb-8">
            <div className="float-left mr-8 mb-6 w-full sm:w-80 lg:w-96">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_colorado-reverse-62/artifacts/uskn1xq9_tn.jpg"
                  alt="Peter Andrew S., CPA & Licensed Mortgage Specialist"
                  className="w-full h-auto object-contain"
                />
                
                {/* Credentials Badge */}
                <div className="absolute top-4 left-4 bg-white rounded-xl shadow-lg p-3">
                  <div className="flex items-center space-x-2">
                    <Award className="w-6 h-6 text-blue-600" />
                    <div>
                      <p className="font-bold text-sm text-gray-900">Licensed Specialist</p>
                      <p className="text-xs text-gray-600">NMLS #2575736</p>
                    </div>
                  </div>
                </div>

                {/* CPA Badge */}
                <div className="absolute top-20 left-4 bg-white rounded-xl shadow-lg p-3">
                  <div className="flex items-center space-x-2">
                    <GraduationCap className="w-6 h-6 text-blue-600" />
                    <div>
                      <p className="font-bold text-sm text-gray-900">Certified CPA</p>
                      <p className="text-xs text-gray-600">Since 2008</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 border-l-4 border-blue-600 p-6 mb-6 rounded-r-lg">
              <h3 className="text-lg font-semibold text-blue-900 mb-3">My Unique Approach</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                I don't just look at reverse mortgages as loan products—I analyze them as 
                <strong> strategic financial planning tools</strong> for tax efficiency and wealth preservation. 
                My CPA background means I understand how a reverse mortgage interacts with your tax situation, estate plans, and cash flow — so the loan structure works with what your advisor and tax professional are already doing, not against it.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                I believe in education and transparency. Whether a reverse mortgage fits your situation or not, 
                my objective is to help you make the most financially intelligent decision 
                for your unique circumstances.
              </p>
              <p className="text-gray-700 leading-relaxed">
                <strong>I'm also not tied to a single lender.</strong> I shop your loan across multiple reverse mortgage lenders to find the pricing and terms that best fit your situation.
              </p>
            </div>

            {/* CPA Disclaimer */}
            <div className="bg-amber-50 border-l-4 border-amber-500 p-5 mb-6 rounded-r-lg">
              <p className="text-sm text-gray-800 leading-relaxed">
                <strong>Important:</strong> While I maintain an active CPA license, I'm acting strictly as your Mortgage Loan Originator on this transaction, not your personal CPA. I use my analytical background to ensure your loan structure integrates with the work your current tax advisor is already doing for you.
              </p>
            </div>

            <p className="text-gray-600 mb-6 leading-relaxed">
              I got into reverse mortgages after years of watching retirees make decisions about their home equity in isolation from the rest of their financial plan — sometimes with good advice, often with none at all. As a CPA, I kept seeing the same gap: mortgage officers who understood the loan but not the tax and estate consequences, and financial advisors who understood the plan but not the nuances of reverse mortgages. I wanted to be the person who could sit across the table from a Colorado homeowner and speak to both sides at once — what this does to your taxes, your kids' inheritance, and your peace of mind, not just your monthly cash flow.
            </p>

            <p className="text-gray-600 mb-8 leading-relaxed">
              With over 20 years of experience in financial services, I've helped Colorado 
              families optimize their retirement income, minimize tax burdens, and preserve wealth for 
              the next generation—all while staying in the homes they love.
            </p>
          </div>

          {/* Clear float */}
          <div className="clear-both"></div>

          {/* Credentials - Fixed capitalization */}
          <div className="bg-white rounded-xl p-8 shadow-lg">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Credentials & Expertise</h3>
            <div className="space-y-4">
              {[
                'Licensed mortgage specialist (NMLS #2575736)',
                'Certified public accountant (CPA)',
                'Expert in tax-efficient retirement strategies',
                'Specialized in wealth preservation through reverse mortgages',
                'Access to multiple lenders to find the best solution for you'
              ].map((credential) => (
                <div key={credential} className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700">{credential}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
